import { IconFont } from '@/components';
import { AlertPrimary } from '@/components/AlertPrimary';
import { SectionHeader } from '@/components/SectionHeader';
import { useFormSyncState } from '@/hooks';
import { PATTERN_NAME } from '@/utils';
import {
  Card,
  Collapse,
  Form,
  Grid,
  Input,
  InputNumber,
  Link,
  Select,
} from '@arco-design/web-react';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useGpuOptions } from '../hooks';
import { WorkloadComponent, WorkloadType } from '../types';
import { DockerItem, useDockerSettingStore } from './useDockerSettingStore';

const FormItem = Form.Item;
const Row = Grid.Row;
const Col = Grid.Col;
const CollapseItem = Collapse.Item;
const { DEPLOYMENT } = WorkloadType;

interface DockerSettingProps extends WorkloadComponent {
  moreLink?: string;
}

/** 添加容器-容器设置 */
export function DockerSetting({ module = DEPLOYMENT, moreLink }: DockerSettingProps) {
  const { t } = useTranslation();
  const msg = {
    container_setting: t('components:container.settings'),
    container_setting_tips: t('components:set.container.image.name.type.and.computing.resources'),
    image: t('components:image'),
    image_placeholder: t('components:image.name.or.path.e.g.nginx.latest.2'),
    image_tips_1: t(
      'components:if.you.need.to.use.private.image.service.you.need.to.create.an.image.service.secret.first',
    ),
    image_tips_2: t('components:learn.more'),
    image_addd: t('components:please.add.image.first'),
    advance_setting: t('common:advanced.settings'),
    container_name: t('components:container.name'),
    container_type: t('components:container.type'),
    container_name_tips: t(
      'common:name.can.only.contain.lowercase.letters.numbers.and.hyphens.must.start.and.end.with.a.lowercase.letter.or.number.maximum.63.characters',
    ),
    resource_limit_tips: t(
      'components:set.container.resource.limits.and.reservations.to.schedule.containers.to.appropriate.nodes',
    ),
    cpu_reserved: t('components:cpu.reservation'),
    cpu_limit: t('components:cpu.limit'),
    memory_reserved: t('components:memory.reservation'),
    memory_limit: t('components:memory.limit'),
    gpu_type: t('components:gpu.type'),
    gpu_limit: t('components:gpu.limit'),
    not_reserved: t('components:no.reservation'),
    not_limited: t('common:no.limit'),
    work_container: t('components:work.container'),
    init_container: t('components:init.containers'),
    no_zero: t('components:resource.reservation.and.limit.cannot.be.set.to.0'),
  };

  const [form] = Form.useForm<DockerItem>();
  const { dockerSetting, updateDockerSetting, setvalidateDockerSetting } = useDockerSettingStore();
  useFormSyncState(form, dockerSetting);

  const { gpuOptions } = useGpuOptions({ platform: 'CONTAINER' });
  const renderGpuOptions = (menu: React.ReactNode) => {
    return <StyledMenu>{menu}</StyledMenu>;
  };

  const validate = useCallback(async () => {
    try {
      await form.validate();
      return true;
    } catch (error) {
      return false;
    }
  }, [form]);

  useEffect(() => {
    setvalidateDockerSetting(validate);
  }, [validate, setvalidateDockerSetting]);

  // 没有添加镜像的情况
  const EmptyImageMarkup = (
    <ImageEmpty>
      <ImageEmptyPlaceholder>
        <IconFont name="icon-jingxiangmian" style={{ fontSize: 36 }} />
        <span>{msg.image_addd}</span>
      </ImageEmptyPlaceholder>
    </ImageEmpty>
  );

  // 容器类型 选项
  const containerTypeOptions = [
    { label: msg.work_container, value: 'containers' },
    { label: msg.init_container, value: 'initContainers' },
  ];

  return (
    <Card>
      <Form
        form={form}
        initialValues={dockerSetting}
        onValuesChange={(_, values) => {
          updateDockerSetting(values);
        }}
      >
        <SectionHeader title={msg.container_setting} description={msg.container_setting_tips} />

        <FormItem label={msg.image} layout="vertical" required style={{ marginBottom: 0 }}>
          <Row gutter={8}>
            <Col span={24}>
              <FormItem field="image" layout="vertical" required rules={[{ required: true }]}>
                <Input placeholder={msg.image_placeholder} />
              </FormItem>
            </Col>
          </Row>
        </FormItem>

        <ImageTips>
          <span>{msg.image_tips_1}</span>
          {moreLink && (
            <LinkText
              style={{ color: 'rgb(var(--link-6))', opacity: 1 }}
              onClick={() => {
                window.open(moreLink, '_blank');
              }}
            >
              {msg.image_tips_2}
            </LinkText>
          )}
        </ImageTips>

        <DottedCard>{EmptyImageMarkup}</DottedCard>

        <ArcoCollapse triggerRegion="header" bordered={false}>
          <CollapseItem header={msg.advance_setting} name="1">
            <Row gutter={8} style={{ marginBottom: 0 }}>
              <Col span={12} style={{ marginBottom: 0 }}>
                <FormItem
                  field="containerName"
                  label={msg.container_name}
                  layout="vertical"
                  required
                  help={msg.container_name_tips}
                  rules={[{ required: true, match: PATTERN_NAME }]}
                >
                  <Input />
                </FormItem>
              </Col>
              <Col span={12} style={{ marginBottom: 0 }}>
                <FormItem label={msg.container_type} layout="vertical">
                  <Select
                    showSearch
                    options={containerTypeOptions}
                    value={dockerSetting.containerType}
                    onChange={(value) => updateDockerSetting({ containerType: value })}
                  />
                </FormItem>
              </Col>
            </Row>

            <AlertPrimary
              banner
              type="warning"
              showIcon
              content={msg.resource_limit_tips}
              style={{ marginTop: 4, marginBottom: 20 }}
            />

            <Row gutter={8} style={{ marginBottom: 0 }}>
              <Col span={8}>
                <ResourceCard>
                  <IconFont name="icon-chip" style={{ fontSize: 36 }} />
                  <ResourceBox>
                    <Label>
                      <span className="label">{msg.cpu_reserved}</span>
                      <LabelAction>
                        <InputNumber
                          size="small"
                          suffix="Core"
                          placeholder={msg.not_reserved}
                          value={dockerSetting.cpuReserve}
                          onChange={(value) => updateDockerSetting({ cpuReserve: value })}
                        />
                      </LabelAction>
                    </Label>
                    <Label>
                      <span className="label">{msg.cpu_limit}</span>
                      <LabelAction>
                        <InputNumber
                          size="small"
                          suffix="Core"
                          placeholder={msg.not_limited}
                          value={dockerSetting.cpuLimit}
                          onChange={(value) => updateDockerSetting({ cpuLimit: value })}
                        />
                      </LabelAction>
                    </Label>
                  </ResourceBox>
                </ResourceCard>
              </Col>
              <Col span={8}>
                <ResourceCard>
                  <IconFont name="icon-memory" style={{ fontSize: 36 }} />
                  <ResourceBox>
                    <Label>
                      <span className="label">{msg.memory_reserved}</span>
                      <LabelAction>
                        <InputNumber
                          size="small"
                          suffix="Mi"
                          placeholder={msg.not_reserved}
                          value={dockerSetting.memoryReserve}
                          onChange={(value) => updateDockerSetting({ memoryReserve: value })}
                        />
                      </LabelAction>
                    </Label>
                    <Label>
                      <span className="label">{msg.memory_limit}</span>
                      <LabelAction>
                        <InputNumber
                          size="small"
                          suffix="Mi"
                          placeholder={msg.not_limited}
                          value={dockerSetting.memoryLimit}
                          onChange={(value) => updateDockerSetting({ memoryLimit: value })}
                        />
                      </LabelAction>
                    </Label>
                  </ResourceBox>
                </ResourceCard>
              </Col>
              <Col span={8}>
                <ResourceCard>
                  <IconFont name="icon-GPU" style={{ fontSize: 36 }} />
                  <ResourceBox>
                    <Label>
                      <span className="label">{msg.gpu_type}</span>
                      <LabelAction>
                        <Select
                          size="small"
                          options={gpuOptions}
                          value={dockerSetting.gpuType}
                          onChange={(value) => updateDockerSetting({ gpuType: value })}
                          style={{ height: 24 }}
                          showSearch
                          allowClear
                          dropdownRender={renderGpuOptions}
                        />
                      </LabelAction>
                    </Label>
                    <Label>
                      <span className="label">{msg.gpu_limit}</span>
                      <LabelAction>
                        <InputNumber
                          size="small"
                          placeholder={msg.not_limited}
                          value={dockerSetting.gpuLimit}
                          onChange={(value) => updateDockerSetting({ gpuLimit: value })}
                        />
                      </LabelAction>
                    </Label>
                  </ResourceBox>
                </ResourceCard>
              </Col>
            </Row>

            {(dockerSetting.cpuReserve === 0 ||
              dockerSetting.cpuLimit === 0 ||
              dockerSetting.memoryReserve === 0 ||
              dockerSetting.memoryLimit === 0) && (
              <span style={{ fontSize: 12, color: 'var(--error-color)' }}>{msg.no_zero}</span>
            )}
          </CollapseItem>
        </ArcoCollapse>
      </Form>
    </Card>
  );
}

/** ============================ 样式 ==================================== */
const SubText = styled.p`
  span {
    color: #000;
    opacity: 0.45;
  }
`;

const ImageTips = styled(SubText)`
  margin-top: 0.5rem;
  margin-bottom: 1rem;
  a {
    text-decoration: underline;
  }
`;

const LinkText = styled(Link)`
  color: rgb(var(--link-6));
  opacity: 1;
  &:hover {
    text-decoration: underline;
  }
`;

const DottedCard = styled(Card)`
  border: 1px dashed #dadada;
`;

const ImageEmpty = styled.div`
  background-color: #f9f9f9;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ImageEmptyPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  opacity: 0.45;
`;

const ArcoCollapse = styled(Collapse)`
  /* 自带的 icon */
  .arco-collapse-item-icon-hover {
    left: 0;
  }
  /* 折叠面板的 header */
  .arco-collapse-item-header {
    background: #fff !important; /* 被全局的 !important 影响了,所以在这里强制转换过来了 */
    padding-left: 14px;
    border-bottom: none;
  }
  /* 折叠面板的 content */
  .arco-collapse-item-content {
    background: #fff !important; /* 被全局的 !important 影响了,所以在这里强制转换过来了 */
  }
`;

/** 容器的资源限制与资源预留 */
const ResourceCard = styled.div`
  background-color: #f9f9f9;
  display: flex;
  align-items: center;
  justify-content: space-around;
  padding: 8px;
  i.iconfont {
    opacity: 0.45;
    flex-shrink: 0;
  }
`;

const ResourceBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end; /* Updated for consistency */
  gap: 8px;
`;

const Label = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: space-between;

  span.label {
    font-size: 12px;
    flex-wrap: nowrap;
    flex-shrink: 0;
  }
`;
const LabelAction = styled.div<{ $isWide?: boolean }>`
  width: ${(props) => (props.$isWide ? '100%' : '136px')};

  .arco-select-view,
  .arco-select-view-selector {
    height: 24.5px;
  }
  .arco-select,
  .arco-input {
    font-size: 12px;
  }
`;
const StyledMenu = styled.div`
  .arco-select-option {
    font-size: 12px;
  }
`;
